import React, { type PropsWithChildren } from 'react';
import { Inline, Box, xcss } from '@atlaskit/primitives';
import { AsyncLocationlessDialog } from '@atlassian/jira-board-location-dialogs-locationless/src/async';
import { useBoardId } from '@atlassian/jira-board-settings-common';
import type { FetchError } from '@atlassian/jira-fetch';
import { Redirect } from '@atlassian/jira-router';
import { AsyncNoSoftwareLicense } from '@atlassian/jira-spa-apps-common/src/error-screens/no-software-license/async';
import { checkLicense } from '@atlassian/jira-spa-apps-common/src/interceptors/license';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { useIsUserLocatedBoard, useIsKanbanBoard, useIsBacklogRoute } from '../../controllers';
import { useBoardDetailsData, useIsJSMBoard } from '../../services';
import { Header } from './header';
import Menu from './menu';
import { useConfigQuery } from './utils';

const BoardNotFoundRedirect = () => <Redirect to="/jira/projects?showFlag=jsw.board-not-found" />;

const SettingsContainerContent = ({ children }: PropsWithChildren) => {
	const isJsm = useIsJSMBoard();
	const isUserLocatedBoard = useIsUserLocatedBoard();
	const isKanban = useIsKanbanBoard();
	const [configQuery] = useConfigQuery();
	const { data, error } = useBoardDetailsData();
	const isBacklog = useIsBacklogRoute();
	const isBacklogInKanban = isKanban && isBacklog;
	const tenantContext = useTenantContext();
	const hasSoftwareLicense = checkLicense(tenantContext, 'software');
	const boardId = useBoardId();

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const fetchError = error as FetchError;

	if (!hasSoftwareLicense) {
		return <AsyncNoSoftwareLicense />;
	}

	if (!boardId) {
		return <BoardNotFoundRedirect />;
	}

	if (data && !data.location) {
		// board is locationless
		return <AsyncLocationlessDialog rapidViewId={data.id} />;
	}

	if (isBacklogInKanban || fetchError?.statusCode === 404) {
		return <BoardNotFoundRedirect />;
	}

	return (
		<Box paddingInline="space.500">
			<Header />
			<Inline>
				<Menu
					isJsm={isJsm}
					isUserLocatedBoard={isUserLocatedBoard}
					isKanban={isKanban}
					currentConfigQuery={configQuery}
				/>
				<Box paddingBlockStart="space.300" paddingBlockEnd="space.250" xcss={pageStyles}>
					{children}
				</Box>
			</Inline>
		</Box>
	);
};

const pageStyles = xcss({
	flexGrow: 1,
	minWidth: '0',
});

export default SettingsContainerContent;
